import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Download, Lock, Unlock, ArrowLeft, User, ChevronRight, Copy, Check,
  Zap, DollarSign, Code, Loader, X, Star, MoreVertical
} from 'lucide-react';
import ModelLogo, { getCategoryIcon } from './ModelLogo';
import { useModels } from '../hooks/useModels';
import VideoPlayer from './VideoPlayer';
import ComparisonCardActions from './ComparisonCardActions';
import { showToast } from './Toast';
import {
  trackPageView,
  trackVote,
  trackDownload,
  trackEvent
} from '../utils/GoogleAnalytics';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

// ==========================================
// HELPER FUNCTIONS
// ==========================================
const formatNumber = (num) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  return num.toString();
};

const formatLatency = (ms) => {
  if (!ms || ms === 0) return '-';
  if (ms >= 1000) return (ms / 1000).toFixed(1) + 's';
  return Math.round(ms) + 'ms';
};

const formatCost = (cost) => {
  if (!cost || cost === 0) return '$0';
  if (cost < 0.001) return '<$0.001';
  if (cost < 0.01) return '$' + cost.toFixed(4);
  return '$' + cost.toFixed(3);
};

const formatDate = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// ==========================================
// EXPANDABLE PROMPT COMPONENT
// ==========================================
function ExpandablePrompt({ prompt, maxLength = 100 }) {
  const [expanded, setExpanded] = useState(false);
  const needsExpansion = prompt && prompt.length > maxLength;

  const displayText = expanded || !needsExpansion
    ? prompt
    : prompt.substring(0, maxLength) + '...';

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-2 sm:p-3">
      <p className="text-sm font-medium text-gray-700 leading-relaxed break-words">
        {displayText || 'No prompt provided'}
        {needsExpansion && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="ml-2 text-blue-600 hover:text-blue-800 font-semibold text-xs inline-flex items-center"
          >
            {expanded ? 'Show less' : 'Show more'}
            <ChevronRight
              size={14}
              className={`ml-0.5 transition-transform ${expanded ? 'rotate-90' : ''}`}
            />
          </button>
        )}
      </p>
    </div>
  );
}

// ==========================================
// COPY BUTTON COMPONENT
// ==========================================
function CopyButton({ text, className = '' }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      showToast('Copied to clipboard!', 'success');
    } catch (err) {
      showToast('Failed to copy', 'error');
    }
  };

  return (
    <button
      onClick={handleCopy}
      className={`p-1.5 rounded bg-gray-800 hover:bg-gray-700 transition-colors ${className}`}
      title={copied ? 'Copied!' : 'Copy code'}
    >
      {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} className="text-gray-400" />}
    </button>
  );
}

// ==========================================
// STATS OVERLAY - Matching ResultsHome
// ==========================================
function StatsOverlay({ results, category, findModel, isMobile = false }) {
  if (!results || results.length < 2) return null;

  const [result1, result2] = results;
  const model1Name = result1?.model || result1?.model_name || 'Model A';
  const model2Name = result2?.model || result2?.model_name || 'Model B';

  const isModelFailed = (result) => {
    if (!result) return true;
    // Only check the error field, not the response content
    const error = result.error || '';
    if (!error) return false;  // No error = success

    const errorLower = error.toLowerCase();
    const errorKeywords = ['error:', 'failed', 'exception', 'quota', 'rate_limit', 'timeout', 'unauthorized'];
    return errorKeywords.some(kw => errorLower.includes(kw)) || error.length > 0;
  };

  const model1Failed = isModelFailed(result1);
  const model2Failed = isModelFailed(result2);
  const anyFailed = model1Failed || model2Failed;

  const latency1 = result1?.latency_ms || (result1?.latency ? result1.latency * 1000 : 0);
  const latency2 = result2?.latency_ms || (result2?.latency ? result2.latency * 1000 : 0);
  const cost1 = result1?.cost || 0;
  const cost2 = result2?.cost || 0;

  const speedWinner = anyFailed ? 0 : (latency1 && latency2 ? (latency1 < latency2 ? 1 : latency2 < latency1 ? 2 : 0) : 0);
  const costWinner = anyFailed ? 0 : (cost1 && cost2 ? (cost1 < cost2 ? 1 : cost2 < cost1 ? 2 : 0) : 0);

  const getThirdMetric = () => {
    const isImageGen = category === 'IMAGE_GENERATION';
    const isVideoGen = category === 'IMAGE_TO_VIDEO' || category === 'VIDEO_GENERATION' || category === 'TEXT_TO_VIDEO';
    const isCodeGen = category === 'CODE_GENERATION';

    if (isImageGen) {
      const res1 = result1?.resolution;
      const res2 = result2?.resolution;
      if (res1 || res2) {
        return { label: 'Size', value1: res1 || '-', value2: res2 || '-', winner: 0 };
      }
      return null;
    }

    if (isVideoGen) {
      const dur1 = result1?.video_duration;
      const dur2 = result2?.video_duration;
      if (dur1 || dur2) {
        const winner = anyFailed ? 0 : (dur1 > dur2 ? 1 : dur2 > dur1 ? 2 : 0);
        return { label: 'Duration', value1: dur1 ? `${dur1}s` : '-', value2: dur2 ? `${dur2}s` : '-', winner };
      }
      return null;
    }

    if (isCodeGen) {
      const lines1 = result1?.code_lines;
      const lines2 = result2?.code_lines;
      const winner = anyFailed ? 0 : (lines1 && lines2 ? (lines1 > lines2 ? 1 : lines2 > lines1 ? 2 : 0) : 0);
      return { label: 'Lines', value1: lines1 != null ? lines1 : '-', value2: lines2 != null ? lines2 : '-', winner };
    }

    const words1 = result1?.word_count;
    const words2 = result2?.word_count;
    if (words1 != null || words2 != null) {
      const winner = anyFailed ? 0 : (words1 && words2 ? (words1 > words2 ? 1 : words2 > words1 ? 2 : 0) : 0);
      return { label: 'Words', value1: words1 != null ? words1 : '-', value2: words2 != null ? words2 : '-', winner };
    }
    return null;
  };

  const thirdMetric = getThirdMetric();
  const thirdMetricWinner = thirdMetric?.winner || 0;

  const model1 = findModel ? findModel(model1Name) : null;
  const model2 = findModel ? findModel(model2Name) : null;

  const getCellStyle = (isWinner, isFailed = false) => {
    if (isFailed) return 'text-red-400';
    if (isWinner) return 'bg-green-100 text-green-700';
    return 'text-gray-600';
  };

  // Mobile version
  if (isMobile) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden mb-3">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="py-2 px-2 text-left w-24"></th>
              <th className="py-2 px-1 text-center">
                <div className="flex items-center justify-center gap-1">
                  <Zap size={12} className="text-amber-500" />
                  <span className="text-[10px] text-gray-500 font-semibold">SPEED</span>
                </div>
              </th>
              <th className="py-2 px-1 text-center">
                <div className="flex items-center justify-center gap-1">
                  <DollarSign size={12} className="text-green-500" />
                  <span className="text-[10px] text-gray-500 font-semibold">COST</span>
                </div>
              </th>
              {thirdMetric && (
                <th className="py-2 px-1 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Code size={12} className="text-blue-500" />
                    <span className="text-[10px] text-gray-500 font-semibold">{thirdMetric.label.toUpperCase()}</span>
                  </div>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            <tr className="bg-gray-50/50">
              <td className="py-2.5 px-2">
                <div className="flex items-center gap-1.5">
                  <ModelLogo model={model1} modelName={model1Name} size="xs" />
                  <span className={`text-xs font-medium truncate max-w-[60px] ${model1Failed ? 'text-red-400' : 'text-gray-700'}`}>
                    {model1Name.split(' ')[0]}
                  </span>
                </div>
              </td>
              <td className="py-2.5 px-1 text-center">
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${getCellStyle(speedWinner === 1, model1Failed)}`}>
                  {model1Failed ? '-' : formatLatency(latency1)}
                </span>
              </td>
              <td className="py-2.5 px-1 text-center">
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${getCellStyle(costWinner === 1, model1Failed)}`}>
                  {model1Failed ? '-' : formatCost(cost1)}
                </span>
              </td>
              {thirdMetric && (
                <td className="py-2.5 px-1 text-center">
                  <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${getCellStyle(thirdMetricWinner === 1, model1Failed)}`}>
                    {model1Failed ? '-' : thirdMetric.value1}
                  </span>
                </td>
              )}
            </tr>
            <tr>
              <td className="py-2.5 px-2">
                <div className="flex items-center gap-1.5">
                  <ModelLogo model={model2} modelName={model2Name} size="xs" />
                  <span className={`text-xs font-medium truncate max-w-[60px] ${model2Failed ? 'text-red-400' : 'text-gray-700'}`}>
                    {model2Name.split(' ')[0]}
                  </span>
                </div>
              </td>
              <td className="py-2.5 px-1 text-center">
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${getCellStyle(speedWinner === 2, model2Failed)}`}>
                  {model2Failed ? '-' : formatLatency(latency2)}
                </span>
              </td>
              <td className="py-2.5 px-1 text-center">
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${getCellStyle(costWinner === 2, model2Failed)}`}>
                  {model2Failed ? '-' : formatCost(cost2)}
                </span>
              </td>
              {thirdMetric && (
                <td className="py-2.5 px-1 text-center">
                  <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${getCellStyle(thirdMetricWinner === 2, model2Failed)}`}>
                    {model2Failed ? '-' : thirdMetric.value2}
                  </span>
                </td>
              )}
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // Desktop version
  return (
    <div
      className="absolute right-0 w-[160px] z-10 pointer-events-auto hidden sm:block"
      style={{
        background: 'linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,1) 15%)',
        top: '12px',
        bottom: '12px'
      }}
    >
      <div className="ml-4 h-full bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden flex flex-col">
        <div className="px-3 py-2 border-b border-gray-100 flex-shrink-0">
          <div className="flex justify-around items-center">
            <div className={model1Failed ? 'opacity-50' : ''}>
              <ModelLogo model={model1} modelName={model1Name} size="sm" />
            </div>
            <div className={model2Failed ? 'opacity-50' : ''}>
              <ModelLogo model={model2} modelName={model2Name} size="sm" />
            </div>
          </div>
        </div>

        <div className="flex-1 flex flex-col divide-y divide-gray-100">
          <div className="flex-1 p-2 flex flex-col justify-center">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <Zap size={14} className="text-amber-500" />
              <span className="text-xs text-gray-500 font-semibold uppercase">Speed</span>
            </div>
            <div className="flex justify-around">
              <span className={`text-sm font-bold px-2 py-0.5 rounded ${getCellStyle(speedWinner === 1, model1Failed)}`}>
                {model1Failed ? '-' : formatLatency(latency1)}
              </span>
              <span className={`text-sm font-bold px-2 py-0.5 rounded ${getCellStyle(speedWinner === 2, model2Failed)}`}>
                {model2Failed ? '-' : formatLatency(latency2)}
              </span>
            </div>
          </div>

          <div className="flex-1 p-2 flex flex-col justify-center">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <DollarSign size={14} className="text-green-500" />
              <span className="text-xs text-gray-500 font-semibold uppercase">Cost</span>
            </div>
            <div className="flex justify-around">
              <span className={`text-sm font-bold px-2 py-0.5 rounded ${getCellStyle(costWinner === 1, model1Failed)}`}>
                {model1Failed ? '-' : formatCost(cost1)}
              </span>
              <span className={`text-sm font-bold px-2 py-0.5 rounded ${getCellStyle(costWinner === 2, model2Failed)}`}>
                {model2Failed ? '-' : formatCost(cost2)}
              </span>
            </div>
          </div>

          {thirdMetric && (
            <div className="flex-1 p-2 flex flex-col justify-center">
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <Code size={14} className="text-blue-500" />
                <span className="text-xs text-gray-500 font-semibold uppercase">{thirdMetric.label}</span>
              </div>
              <div className="flex justify-around">
                <span className={`text-sm font-bold px-2 py-0.5 rounded ${getCellStyle(thirdMetricWinner === 1, model1Failed)}`}>
                  {model1Failed ? '-' : thirdMetric.value1}
                </span>
                <span className={`text-sm font-bold px-2 py-0.5 rounded ${getCellStyle(thirdMetricWinner === 2, model2Failed)}`}>
                  {model2Failed ? '-' : thirdMetric.value2}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ==========================================
// MOBILE ACTIONS MENU
// ==========================================
function MobileActionsMenu({ comparison, onTogglePrivacy, isOwner }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative flex items-center justify-center">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center"
      >
        <MoreVertical size={16} className="text-gray-500" />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-50 min-w-[160px]">
            <ComparisonCardActions
              comparison={comparison}
              onTogglePrivacy={isOwner ? onTogglePrivacy : null}
              isOwner={isOwner}
            />
          </div>
        </>
      )}
    </div>
  );
}

// ==========================================
// MAIN COMPONENT
// ==========================================
function ResultDetail() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const { models: availableModels } = useModels();

  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [votes, setVotes] = useState({});
  const [userVoted, setUserVoted] = useState(new Set());
  const [showStats, setShowStats] = useState(true);
  const [votingInProgress, setVotingInProgress] = useState(new Set());
  const [imageModal, setImageModal] = useState(null);

  const cardRef = useRef(null);

  const findModel = useCallback((modelName) => {
    if (!modelName || !availableModels) return null;
    const name = modelName.toLowerCase();
    return availableModels.find(m =>
      m.id.toLowerCase() === name ||
      m.name.toLowerCase().includes(name) ||
      name.includes(m.id.toLowerCase())
    );
  }, [availableModels]);

  useEffect(() => {
    loadComparison();
    trackPageView(`/result/${runId}`, 'Result Detail');
  }, [runId]);

  const loadComparison = async () => {
    try {
      // STATIC MODE: load from local JSON file and find by runId
      const response = await fetch('/data/results.json');
      if (!response.ok) throw new Error('Failed to load results data');
      const allResults = await response.json();
      const data = (Array.isArray(allResults) ? allResults : []).find(r => r.run_id === runId);

      if (!data) throw new Error('Comparison not found.');
      setComparison(data);
      trackEvent('Comparison', 'View', data.category);

      if (data.results) {
        const initialVotes = {};
        data.results.forEach(result => {
          if (result) initialVotes[result.id] = { votes: result.vote_count || 0 };
        });
        setVotes(initialVotes);

        if (data.user_vote) {
          setUserVoted(new Set([data.user_vote]));
        }
      }
    } catch (error) {
      console.error('❌ Failed to load:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (resultId, modelName) => {
    if (votingInProgress.has(resultId)) return;

    const token = localStorage.getItem('token');
    if (!token) {
      showToast('Please login to vote', 'warning');
      return;
    }

    setVotingInProgress(prev => new Set(prev).add(resultId));

    const wasVoted = userVoted.has(resultId);

    setUserVoted(prev => {
      const newSet = new Set(prev);
      if (wasVoted) {
        newSet.delete(resultId);
      } else {
        comparison.results?.forEach(result => newSet.delete(result.id));
        newSet.add(resultId);
      }
      return newSet;
    });

    try {
      const response = await fetch(`${API_URL}/benchmark/vote/${resultId}`, {
        method: wasVoted ? 'DELETE' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        trackVote(modelName, 'result_detail');
        const data = await response.json();

        if (data.all_votes) {
          const parsedVotes = {};
          Object.entries(data.all_votes).forEach(([id, voteData]) => {
            const voteNum = typeof voteData === 'object' ? (voteData.votes || 0) : (voteData || 0);
            parsedVotes[id] = { votes: voteNum };
          });
          setVotes(parsedVotes);
        } else if (typeof data.vote_count === 'number') {
          setVotes(prev => ({ ...prev, [resultId]: { votes: data.vote_count } }));
        }
      } else {
        setUserVoted(prev => {
          const newSet = new Set(prev);
          if (wasVoted) newSet.add(resultId);
          else newSet.delete(resultId);
          return newSet;
        });
        showToast('Vote failed. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Vote error:', error);
      setUserVoted(prev => {
        const newSet = new Set(prev);
        if (wasVoted) newSet.add(resultId);
        else newSet.delete(resultId);
        return newSet;
      });
      showToast('Connection error. Please try again.', 'error');
    } finally {
      setVotingInProgress(prev => {
        const newSet = new Set(prev);
        newSet.delete(resultId);
        return newSet;
      });
    }
  };

  const togglePrivacy = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      showToast('Please login to change privacy', 'warning');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/benchmark/${runId}/privacy`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_public: !comparison.is_public })
      });

      if (response.ok) {
        setComparison(prev => ({ ...prev, is_public: !prev.is_public }));
        showToast(`Comparison is now ${!comparison.is_public ? 'public' : 'private'}`, 'success');
      } else {
        showToast('Failed to update privacy', 'error');
      }
    } catch (error) {
      showToast('Failed to update privacy', 'error');
    }
  };

  const openImageModal = (url, model) => setImageModal({ url, model });

  const downloadImage = async (url, modelName) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${modelName.replace(/\s+/g, '_')}_output.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      trackDownload(modelName);
      showToast('Image downloaded!', 'success');
    } catch (error) {
      showToast('Failed to download image', 'error');
    }
  };

  // Loading state
  if (loading) {
    return (
      <main className="min-h-screen pb-4 sm:pb-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col items-center justify-center py-20">
            <Loader className="animate-spin h-12 w-12 text-blue-600 mb-4" />
            <p className="text-gray-600 font-medium">Loading comparison...</p>
          </div>
        </div>
      </main>
    );
  }

  // Error state
  if (error || !comparison) {
    return (
      <main className="min-h-screen pb-4 sm:pb-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center py-20 bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              {error?.includes('private') ? 'Private Comparison' : 'Not Found'}
            </h1>
            <p className="text-gray-600 mb-6">{error || 'Comparison not found'}</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors"
            >
              Back to Home
            </button>
          </div>
        </div>
      </main>
    );
  }

  const isImageGen = comparison.category === 'IMAGE_GENERATION';
  const isVideoGen = comparison.category === 'IMAGE_TO_VIDEO' || comparison.category === 'VIDEO_GENERATION' || comparison.category === 'TEXT_TO_VIDEO';
  const isCodeGen = comparison.category === 'CODE_GENERATION';
  const run = { ...comparison, run_id: runId };

  return (
    <main className="min-h-screen pb-4 sm:pb-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 space-y-3">

        {/* Back Button - Simple */}
        <button
          onClick={() => navigate('/compare')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors py-2"
        >
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">Back to Results</span>
        </button>

        {/* Main Card - Matching ResultsHome exactly */}
        <article
          ref={cardRef}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden border-l-4 border-l-violet-300"
        >
          {/* Header */}
          <div className="px-3 sm:px-4 py-3 border-b border-gray-100">
            {/* Desktop Header */}
            <div className="hidden sm:flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-2.5 py-1 bg-gray-50 rounded-lg">
                  {getCategoryIcon(comparison.category, 16)}
                  <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                    {comparison.category?.replace(/_/g, ' ') || 'Unknown'}
                  </span>
                </div>
                <span className="text-xs text-gray-400">•</span>
                <span className="text-xs text-gray-500">{formatDate(comparison.created_at)}</span>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <User size={14} />
                  <span>{comparison.display_name || comparison.username || 'Anonymous'}</span>
                </div>
                {comparison.is_owner && (
                  <span className="px-2 py-0.5 bg-orange-100 text-orange-600 text-xs font-medium rounded">
                    Yours
                  </span>
                )}
                {comparison.is_public !== undefined && (
                  <span className={`px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1 ${
                    comparison.is_public
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {comparison.is_public ? <Unlock size={10} /> : <Lock size={10} />}
                    {comparison.is_public ? 'Public' : 'Private'}
                  </span>
                )}

                {/* Stats Toggle - Colorful */}
                {comparison.results && comparison.results.length >= 2 && (
                  <button
                    onClick={() => setShowStats(!showStats)}
                    className={`p-1.5 rounded-lg transition-all ${showStats ? 'bg-blue-50' : 'hover:bg-gray-100'}`}
                    title={showStats ? 'Hide stats' : 'Show stats'}
                  >
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                      <rect x="1" y="8" width="3" height="7" rx="1" fill="#93C5FD" />
                      <rect x="6" y="4" width="3" height="11" rx="1" fill="#86EFAC" />
                      <rect x="11" y="1" width="3" height="14" rx="1" fill="#FCA5A5" />
                    </svg>
                  </button>
                )}

                <ComparisonCardActions
                  comparison={run}
                  onTogglePrivacy={comparison.is_owner ? togglePrivacy : null}
                  isOwner={comparison.is_owner}
                />
              </div>
            </div>

            {/* Mobile Header */}
            <div className="sm:hidden">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-lg">
                    {getCategoryIcon(comparison.category, 14)}
                    <span className="text-xs font-semibold text-gray-700 uppercase">
                      {comparison.category?.replace(/_/g, ' ')?.split(' ')[0] || 'Unknown'}
                    </span>
                  </div>
                  <span className="text-xs text-gray-400">{formatDate(comparison.created_at)}</span>
                </div>
                <div className="flex items-center gap-1">
                  {comparison.results && comparison.results.length >= 2 && (
                    <button
                      onClick={() => setShowStats(!showStats)}
                      className={`w-8 h-8 rounded-lg transition-all flex items-center justify-center ${
                        showStats ? 'bg-blue-50' : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <rect x="1" y="8" width="3" height="7" rx="1" fill="#93C5FD" />
                        <rect x="6" y="4" width="3" height="11" rx="1" fill="#86EFAC" />
                        <rect x="11" y="1" width="3" height="14" rx="1" fill="#FCA5A5" />
                      </svg>
                    </button>
                  )}
                  <MobileActionsMenu
                    comparison={run}
                    onTogglePrivacy={togglePrivacy}
                    isOwner={comparison.is_owner}
                  />
                </div>
              </div>
              {/* Mobile user info */}
              <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                <User size={12} />
                <span>{comparison.display_name || comparison.username || 'Anonymous'}</span>
                {comparison.is_owner && (
                  <span className="px-1.5 py-0.5 bg-orange-100 text-orange-600 text-xs font-medium rounded">
                    Yours
                  </span>
                )}
                {comparison.is_public !== undefined && (
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                    comparison.is_public ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {comparison.is_public ? 'Public' : 'Private'}
                  </span>
                )}
              </div>
            </div>

            {/* Prompt with Input Image */}
            <div className="mt-2 flex gap-3 items-start">
              {comparison.input_image_url && ['IMAGE_TO_VIDEO', 'IMAGE_ANALYSIS', 'VISION'].includes(comparison.category) && (
                <img
                  src={comparison.input_image_url}
                  alt="Input"
                  className="w-12 h-12 rounded-lg object-cover flex-shrink-0 border border-gray-200"
                />
              )}
              <div className="flex-1 min-w-0">
                <ExpandablePrompt prompt={comparison.prompt} maxLength={100} />
              </div>
            </div>
          </div>

          {/* Results Grid */}
          <div className="p-3 sm:p-4 relative">
            {/* Mobile Stats Overlay */}
            {showStats && comparison.results && comparison.results.length >= 2 && (
              <div className="sm:hidden">
                <StatsOverlay
                  results={comparison.results}
                  category={comparison.category}
                  findModel={findModel}
                  isMobile={true}
                />
              </div>
            )}

            {!comparison.results || comparison.results.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No model results for this comparison</p>
              </div>
            ) : (
              <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 relative transition-all ${showStats ? 'sm:pr-[170px]' : ''}`}>
                {comparison.results.map((result, idx) => {
                  if (!result) return null;

                  const modelName = result.model || result.model_name || result.model_id || 'Unknown';
                  const resultVotes = votes[result.id]?.votes || 0;
                  const myVote = userVoted.has(result.id);

                  return (
                    <div
                      key={result.id || idx}
                      className={`flex flex-col bg-white rounded-xl p-3 border shadow-sm transition-all ${
                        myVote ? 'border-blue-400 ring-2 ring-blue-200' : 'border-gray-100'
                      }`}
                    >
                      {/* Model Header with Vote */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <ModelLogo model={findModel(modelName)} modelName={modelName} size="sm" />
                          <h3 className="font-bold text-sm truncate max-w-[120px] sm:max-w-[160px]">{modelName}</h3>
                        </div>

                        {/* Vote button - Star icon */}
                        <button
                          onClick={() => handleVote(result.id, modelName)}
                          disabled={votingInProgress.has(result.id)}
                          className={`flex items-center gap-1.5 px-2 py-1 rounded-lg transition-all ${
                            myVote ? 'text-blue-600' : 'text-gray-400 hover:text-gray-600'
                          } ${votingInProgress.has(result.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title={myVote ? 'Remove vote' : 'Vote for this result'}
                        >
                          <Star size={18} fill={myVote ? 'currentColor' : 'none'} />
                          {resultVotes > 0 && (
                            <span className="text-sm font-medium">{formatNumber(resultVotes)}</span>
                          )}
                        </button>
                      </div>

                      {/* Content - SAME HEIGHT AS ResultsHome: h-[240px] sm:h-[280px] */}
                      <div className="flex-1 min-h-0">
                        {isVideoGen ? (
                          <div className="h-[240px] sm:h-[280px]">
                            <VideoPlayer videoUrl={result.video_url || result.output_video_url || result.response} />
                          </div>
                        ) : isImageGen ? (
                          <div
                            className="relative rounded-lg overflow-hidden border border-gray-100 cursor-pointer h-[240px] sm:h-[280px]"
                            onClick={() => openImageModal(result.image_url || result.response, modelName)}
                          >
                            <img
                              src={result.image_url || result.response}
                              alt={`Generated by ${modelName}`}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                        ) : isCodeGen ? (
                          <div className="bg-gray-900 rounded-lg p-2.5 h-[240px] sm:h-[280px] overflow-y-auto relative">
                            <CopyButton text={result.response || ''} className="absolute top-2 right-2 z-10" />
                            <pre className="text-xs text-gray-300 whitespace-pre-wrap break-words font-mono pr-8">
                              {result.response || 'No response'}
                            </pre>
                          </div>
                        ) : (
                          <div className="bg-gray-50 rounded-lg p-2.5 h-[240px] sm:h-[280px] overflow-y-auto relative group">
                            <button
                              onClick={async () => {
                                await navigator.clipboard.writeText(result.response || '');
                                showToast('Copied to clipboard!', 'success');
                              }}
                              className="absolute top-2 right-2 z-10 p-1.5 rounded bg-white border border-gray-200 hover:bg-gray-100 transition-colors opacity-0 group-hover:opacity-100"
                              title="Copy text"
                            >
                              <Copy size={14} className="text-gray-500" />
                            </button>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap break-words pr-8">
                              {result.response || 'No response'}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Desktop Stats Overlay */}
                {showStats && comparison.results && comparison.results.length >= 2 && (
                  <StatsOverlay
                    results={comparison.results}
                    category={comparison.category}
                    findModel={findModel}
                  />
                )}
              </div>
            )}
          </div>
        </article>
      </div>

      {/* Image Modal */}
      {imageModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setImageModal(null)}
        >
          <div className="bg-white rounded-xl p-4 sm:p-6 max-w-4xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl sm:text-2xl font-bold">{imageModal.model}</h3>
              <button onClick={() => setImageModal(null)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            <img
              src={imageModal.url}
              alt={`Full size image by ${imageModal.model}`}
              className="w-full h-auto rounded-lg mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => downloadImage(imageModal.url, imageModal.model)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
              >
                <Download size={18} />
                Download Image
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default ResultDetail;